import { FC, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Grid,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from "@mui/material";
import {
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  Email as EmailIcon,
  Description as DocumentIcon,
} from "@mui/icons-material";
import {
  TBranding,
  TBrandingAsset,
  TBrandingAssetType,
} from "../../../apis/branding/branding.interfaces";
import { useDeleteAssetHook } from "../../../hooks/useBrandingHook";
import { useInstituteContext } from "../../../context/institute.context";
import { logger } from "../../../utils/common";
import AssetUploadDialog from "./assetUploadDialog";

interface BrandingAssetsSectionProps {
  branding?: TBranding;
}

const ASSET_TYPE_CONFIG: Record<
  TBrandingAssetType,
  {
    label: string;
    icon: FC;
    color:
      | "default"
      | "primary"
      | "secondary"
      | "error"
      | "info"
      | "success"
      | "warning";
  }
> = {
  favicon: {
    label: "Favicon",
    icon: ImageIcon,
    color: "primary",
  },
  emailTemplate: {
    label: "Email Template",
    icon: EmailIcon,
    color: "info",
  },
  documentTemplate: {
    label: "Document Template",
    icon: DocumentIcon,
    color: "secondary",
  },
};

const BrandingAssetsSection: FC<BrandingAssetsSectionProps> = ({
  branding,
}) => {
  const { selectedInstitute } = useInstituteContext();
  const deleteAssetMutation = useDeleteAssetHook();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedAssetType, setSelectedAssetType] =
    useState<TBrandingAssetType | null>(null);

  const assets = branding?.assets || [];

  const getAssetByType = (
    type: TBrandingAssetType,
  ): TBrandingAsset | undefined => {
    return assets.find((a) => a.type === type);
  };

  const handleUploadClick = (assetType: TBrandingAssetType) => {
    setSelectedAssetType(assetType);
    setUploadDialogOpen(true);
  };

  const handleDelete = async (assetId: string) => {
    if (!selectedInstitute) return;

    if (
      !window.confirm(
        "Are you sure you want to delete this asset? This action cannot be undone.",
      )
    ) {
      return;
    }

    try {
      await deleteAssetMutation.mutateAsync({
        instituteId: selectedInstitute.id,
        assetId,
      });
      logger.show_success("Asset deleted successfully!");
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : "Failed to delete asset. Please try again.",
      );
    }
  };

  const handleView = (asset: TBrandingAsset) => {
    window.open(asset.fileUrl, "_blank");
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const renderAssetCard = (assetType: TBrandingAssetType) => {
    const config = ASSET_TYPE_CONFIG[assetType];
    const Icon = config.icon;
    const asset = getAssetByType(assetType);

    return (
      <Grid item xs={12} md={4} key={assetType}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
              <Box
                sx={{ color: `${config.color}.main`, display: "inline-flex" }}
              >
                <Icon />
              </Box>
              <Typography variant="h6">{config.label}</Typography>
            </Box>

            {asset ? (
              <Box>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary={asset.fileName}
                      secondaryTypographyProps={{ component: "div" }}
                      secondary={
                        <Box>
                          <Typography variant="caption" display="block">
                            {formatFileSize(asset.fileSize)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Uploaded:{" "}
                            {new Date(asset.uploadedAt).toLocaleDateString()}
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleView(asset)}
                        data-testid={`view-${assetType}-button`}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleDelete(asset.id)}
                        color="error"
                        data-testid={`delete-${assetType}-button`}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<UploadIcon />}
                  onClick={() => handleUploadClick(assetType)}
                  fullWidth
                  sx={{ mt: 1 }}
                  data-testid={`replace-${assetType}-button`}
                >
                  Replace
                </Button>
              </Box>
            ) : (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={2}
                sx={{ py: 2 }}
              >
                <FileIcon sx={{ fontSize: 48, color: "text.secondary" }} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                >
                  No {config.label.toLowerCase()} uploaded
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<UploadIcon />}
                  onClick={() => handleUploadClick(assetType)}
                  data-testid={`upload-${assetType}-button`}
                >
                  Upload {config.label}
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Typography variant="h6">Branding Assets</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Manage additional branding assets such as favicon, email
              templates, and document templates.
            </Typography>
            <Grid container spacing={2}>
              {Object.keys(ASSET_TYPE_CONFIG).map((type) =>
                renderAssetCard(type as TBrandingAssetType),
              )}
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {uploadDialogOpen && selectedAssetType && (
        <AssetUploadDialog
          open={uploadDialogOpen}
          onClose={() => {
            setUploadDialogOpen(false);
            setSelectedAssetType(null);
          }}
          assetType={selectedAssetType}
          existingAsset={getAssetByType(selectedAssetType)}
        />
      )}
    </>
  );
};

export default BrandingAssetsSection;
